import Pusher from "pusher-js";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { getBaseUrl, isLoggedInUser, isPatientChatMessage, requestNotificationPermission, showBrowserNotification, removeHtmlTags, buildNotificationPayload, storeNotificationPayload } from "../helpers/utility";
import baseApiHelper, { updateStoreThroughPusher } from "../services/baseApiHelper";
import { chatApi, useGetNursingHomeLocationsQuery } from "../services/chatService";
import { useGroupChatContext } from "./groupchatContext";
const PusherContext = React.createContext();

const usePusherContext = () => {
  return useContext(PusherContext);
}

const notifyTypes = ['checkin', 'checkout', 'sms_enabled', 'sms_disabled', 'call_in_app_enabled', 'call_in_app_disabled', 'call_phone_enabled', 'call_phone_disabled']

const PusherWrapper = ({ children }) => {

    const pusherRef = useRef();

    const questionUpdateTimeout = useRef(null);
    const acknowledgeTimeout = useRef(null);

    const [nursingHomeLocations, setNursingHomeLocations] = useState([]);
    const [channelNames, setChannelNames] = useState([]);
    const [telemedPusherData, setTelemedPusherData] = useState(null);
    const [pusherData, setPusherData] = useState(null);

    if(!getBaseUrl()?.pusher_settings?.key){
        localStorage.removeItem('base_url');
    }

    const pusher = new Pusher(getBaseUrl()?.pusher_settings?.key, {
        cluster: getBaseUrl()?.pusher_settings?.cluster,
    });


    const groupChatContext = useGroupChatContext();

    const {authData, selectedChat, setSelectedChat, selectedLocations, selectedChatTab, setMessages} = useMemo(() => ({
        authData: groupChatContext.authData,
        selectedChat: groupChatContext.selectedChat,
        setSelectedChat: groupChatContext.setSelectedChat,
        selectedLocations: groupChatContext.selectedLocations,
        selectedChatTab: groupChatContext.selectedChatTab,
        setMessages: groupChatContext.setMessages
    }));

    const selectedChatRef = useRef(selectedChat)
    const authDataRef = useRef(authData)

    useEffect(()=>{
        selectedChatRef.current = selectedChat
    },[selectedChat])

    useEffect(()=>{
        authDataRef.current = authData
    },[authData])

    const [getNursingHomeLocations] = chatApi.endpoints.getNursingHomeLocations.useLazyQuery();

    const fetchNursingLocations = async() => {
        const response = await getNursingHomeLocations();
        setNursingHomeLocations(response.data);
    }

    useEffect(()=>{
        if(authData && Object.keys(authData).length && !authData?.is_provider){
            fetchNursingLocations()
        }
        if(authData?.is_provider){
            setChannelNames([authData?.uuid]);
        }
        
        // Request notification permission when user is authenticated
        if(authData && Object.keys(authData).length){
            requestNotificationPermission();
        }
    }, [authData])

    useEffect(()=>{
        if(!authData?.is_provider && nursingHomeLocations?.length){
            let tempChannelNames = [];
            nursingHomeLocations.forEach((location)=>{
                tempChannelNames.push(location?.uuid)
            })
            setChannelNames(tempChannelNames)
        }
    }, [nursingHomeLocations])


    const onUpdatePatientLocation = (data) => {
        if(selectedChatTab == 'patient_chats'){
            // if the patient chat is open and the location is changed, then we need to close the chat
            if(selectedChatRef.current?.pid == data.pid){
                setSelectedChat({});
            }
            baseApiHelper().updatePatientChatCacheThroughPusher(data, selectedLocations);
        }
    }

    // Show notification immediately when Pusher event arrives (not in useEffect)
    // React defers re-renders when tab is backgrounded, so useEffect runs too late
    const tryShowNotificationForMessage = (data) => {
        if (!data?.message) return;
        if (isLoggedInUser(authDataRef.current, data)) return;
        const currentChat = selectedChatRef.current;
        const isCurrentChatOpened = isPatientChatMessage(currentChat)
            ? data.pid == currentChat?.pid
            : (currentChat?.location_id == data.location?.id && !data.pid);
        if (isCurrentChatOpened) return;

        const messageText = data.message?.text || "";
        const cleanMessageText = removeHtmlTags(messageText).substring(0, 100);
        const senderName = data.sender;
        const notificationTitle = isPatientChatMessage(data)
            ? `New message from ${senderName}`
            : `New message from ${data.location?.name || "Location"}`;

        const notificationPayload = buildNotificationPayload(data);
        storeNotificationPayload(notificationPayload);
        showBrowserNotification(notificationTitle, {
            body: cleanMessageText || "You have a new message",
            tag: `message-${data.id}`,
            requireInteraction: false,
            data: notificationPayload,
        });
    }

    useEffect(() => {
        if(channelNames?.length){
            try {
                if(!pusherRef.current) {
                    let channels = channelNames.map(channelName => pusher.subscribe(channelName));
                    channels?.forEach((channel)=>{
                        channel.bind("nursing_home_chat", function (data) {
                            console.warn(":PUSHER RECEIVED ::", data);
                            if(data?.type == 'user_logged_out'){
                                toast.warn('Your session has expired, you will be redirected to Login..');
                                setTimeout(() => {
                                    window.location = '/auth/login';
                                }, 5000);
                            }
                            else if (data?.type === "nursing_chat_questions_updated") {
                                if (questionUpdateTimeout.current) {
                                    clearTimeout(questionUpdateTimeout.current);
                                }
                                questionUpdateTimeout.current = setTimeout(() => {
                                    baseApiHelper().invalidateTags(["ProblemQuestions"]);
                                    questionUpdateTimeout.current = null;
                                }, 3000);
                            } 
                            else if(notifyTypes.includes(data?.type)){
                                baseApiHelper().invalidateTags(["TaggedProvider"]);
                                if(data?.type == 'checkin' || data?.type == 'checkout'){
                                    baseApiHelper().invalidateTags(["PracticeLocation"]);
                                    if(!isPatientChatMessage(selectedChatRef.current)){
                                        baseApiHelper().invalidateTags(["providerLocationChats"]);
                                    }
                                }
                            } else if (data?.type === "marked_acknowledged") {
                                if (acknowledgeTimeout.current) {
                                    clearTimeout(acknowledgeTimeout.current);
                                }
                                acknowledgeTimeout.current = setTimeout(() => {
                                    // baseApiHelper().updatePatientChatsCacheThroughPusher(data);
                                    setMessages((prevMessages) => {
                                        try {
                                            const foundIndex = prevMessages?.data?.findIndex((msg) => `${msg?.id}` === `${data?.message_id}`);
                                            if (foundIndex > -1) {
                                                const newData = [...prevMessages.data];
                                                newData[foundIndex] = {
                                                    ...newData[foundIndex],
                                                    ...data,
                                                    acknowledged_by_user_id: data.user,
                                                };
                                                const newMessages = { ...prevMessages, data: newData };
                                                return newMessages;
                                            }
                                            return prevMessages;
                                        } catch (error) {
                                            console.error("Error in set Messages:", error);
                                            return prevMessages;
                                        }
                                    });
                                    acknowledgeTimeout.current = null;
                                }, 1500);
                            }
                            else if(data?.type === "patient_location_changed"){
                                onUpdatePatientLocation(data)
                            }
                            else {
                                tryShowNotificationForMessage(data);
                                setPusherData(data);
                            }
                        });

                        channel.bind("my_event", function (data) {
                            console.warn("My event PUSHER RECEIVED ::", data);
                            if(["telemed_call_ended", "telemed_call_finished", "telemed_call_joined", "telemed_call_on_hold", "telemed_call_started", "telemed_call_left", "telemed_visitor_added_data", "telemed_user_added_to_room", "telemed_call_checked_in", "telemed_call_checked_out", "telemed_call_cancelled"].includes(data?.msgtype)){
                                if(data?.msgtype == "telemed_call_left" || data?.msgtype == "telemed_call_finished"){
                                    setTelemedPusherData({...data, isCloseCallWindow: true});
                                }
                                else{
                                    setTelemedPusherData(data);
                                }
                                baseApiHelper().invalidateTags(["vezoSessionsListing", "vezoSessionsWaitingList"]);
                            }
                            else if (["share_data_preparing","share_data_prepared"].includes(data?.msgtype)){
                                baseApiHelper().invalidateTags(["ShareData"]);
                            }
                        });
                    })
                    // channel = pusher.subscribe(authData?.is_provider ?  authData?.uuid : authData?.location_uuid);
                }
                if(channelNames.length){
                    pusherRef.current = channelNames[0];
                }

                return () => {
                    channelNames?.forEach((channelName) => pusher.unsubscribe(channelName));
                    pusher.disconnect();
                    pusherRef.current = null;
                }
            } catch (error) {
                console.log(error)
            }
        }
    }, [channelNames]);

    useEffect(()=>{
        if(pusherData && pusherData.type != 'checkin' && pusherData.type != 'checkout'){
            localStoreUpdateForRecentChat();
        }
    }, [pusherData])
    
    const localStoreUpdateForRecentChat = () => {
        
        const newMsg = {id: pusherData.id, patient: pusherData.patient, pid: pusherData.pid, message: pusherData.message,
            nurse_id: pusherData.nurse_id, user_id: pusherData.user_id, created: pusherData.created, location: pusherData.location,
            mentions: pusherData.mentions, urgent: pusherData.urgent
        };
        
        if(selectedChatTab == 'patient_chats'){
            if(selectedChatRef.current?.pid != newMsg.pid){
                newMsg.isMsgFromDifferentThread = true;
            }
            if(selectedChatRef.current?.pid == newMsg.pid){
                newMsg.patient = selectedChatRef.current?.patient;
            }

            baseApiHelper().updatePatientChatCacheThroughPusher(newMsg, selectedLocations);
        }
        else if(selectedChatTab == 'location_chats'){
            if(selectedChatRef.current?.location_id != newMsg.location?.id){
                newMsg.isMsgFromDifferentThread = true;
            }
            else{  
                newMsg.isMsgFromDifferentThread = false;
            }

            // don't update the chat if the message is a broadcast message and the user is logged in
            if(!(newMsg.message?.event_type == 'broadcast_message' && isLoggedInUser(authData, newMsg))){
                baseApiHelper().updateLocationChatCacheThroughPusher(newMsg, selectedLocations);
            }
        }

        baseApiHelper().invalidateTags(["UnreadMessageCount"]);
    }

    const defaultValues = {
        pusher,
        pusherData,
        setPusherData,
        telemedPusherData,
        setTelemedPusherData
    };

    return (
        <PusherContext.Provider value={defaultValues}>
            {children}
        </PusherContext.Provider>
    );
}


export { PusherWrapper as default, usePusherContext, PusherContext};